import { z } from "zod";
// ---------------------------------------------------------------------------------------------
// Envelope — the one record type. Stored verbatim; `kind` is open-ended.
// ---------------------------------------------------------------------------------------------
export const Envelope = z.object({
    v: z.literal(1),
    deviceId: z.string().min(1),
    runId: z.string().optional(),
    seq: z.number().int().nonnegative(),
    ts: z.number(),
    kind: z.string().min(1),
    payload: z.unknown(),
});
// ---------------------------------------------------------------------------------------------
// Device → server frames (JSON text frames). Blobs ride as binary frames:
//   [u32 BE headerLen][JSON BlobHeader][raw bytes]
// ---------------------------------------------------------------------------------------------
export const HelloFrame = z.object({
    type: z.literal("hello"),
    deviceId: z.string().min(1),
    nickname: z.string().default(""),
    seq: z.number().int().nonnegative().default(0),
    ts: z.number().optional(),
    /** Install identity: side-by-side variant installs on one physical device present
     *  (deviceId, applicationId); the server keys everything on deviceKey() of the pair. */
    applicationId: z.string().optional(),
    /** Variant build stamp (BuildConfig VARIANT_EXP / VARIANT_ID / GIT_SHA). */
    variant: z.object({ exp: z.string().optional(), id: z.string().optional(), gitSha: z.string().optional() }).partial().optional(),
    app: z
        .object({
        versionName: z.string().optional(),
        versionCode: z.number().optional(),
        applicationId: z.string().optional(),
        /** Build identity for EVERY build, baseline included (#95). Distinct from variant.gitSha,
         *  which only stamped variant builds send — this is the one a tester's install carries.
         *  Absent from this schema it would be silently stripped here, arrive nowhere, and render
         *  as a blank identity that looks exactly like a correct one. */
        gitSha: z.string().optional(),
    })
        .partial()
        .optional(),
    device: z
        .object({ model: z.string().optional(), manufacturer: z.string().optional(), sdk: z.number().optional() })
        .partial()
        .optional(),
    capabilities: z.object({ screenshots: z.boolean().optional() }).partial().optional(),
});
/**
 * Storage identity of one install. Legacy devices (no applicationId) keep their bare deviceId —
 * fully backwards compatible; an install that reports its applicationId is keyed on the pair, so
 * side-by-side variant installs of the same app on one physical device never collide on
 * (device_id, seq), secrets, or derived rows.
 */
export function deviceKey(deviceId, applicationId) {
    const a = (applicationId ?? "").trim();
    return a ? `${deviceId}@${a}` : deviceId;
}
export const EventsFrame = z.object({ type: z.literal("events"), events: z.array(Envelope) });
export const PingFrame = z.object({ type: z.literal("ping"), ts: z.number().optional() });
export const BlobQueryFrame = z.object({ type: z.literal("blob.query"), sha256: z.array(z.string()) });
export const CommandResultFrame = z.object({
    type: z.literal("command.result"),
    id: z.string(),
    ok: z.boolean(),
    result: z.unknown().optional(),
});
export const DeviceFrame = z.discriminatedUnion("type", [
    HelloFrame,
    EventsFrame,
    PingFrame,
    BlobQueryFrame,
    CommandResultFrame,
]);
export const BlobHeader = z.object({
    type: z.literal("blob"),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    mime: z.string().default("application/octet-stream"),
    bytes: z.number().int().nonnegative().optional(),
    deviceId: z.string().optional(),
});
// ---------------------------------------------------------------------------------------------
// Server → device
// ---------------------------------------------------------------------------------------------
export const HelloOkFrame = z.object({ type: z.literal("hello.ok"), serverTs: z.number(), lastSeq: z.number() });
export const AckFrame = z.object({ type: z.literal("ack"), seq: z.number() });
export const BlobAckFrame = z.object({ type: z.literal("blob.ack"), sha256: z.string() });
export const BlobHaveFrame = z.object({ type: z.literal("blob.have"), sha256: z.array(z.string()) });
export const PongFrame = z.object({ type: z.literal("pong"), ts: z.number() });
/** Reserved — never sent in v1. */
export const CommandFrame = z.object({
    type: z.literal("command"),
    id: z.string(),
    name: z.string(),
    args: z.unknown().optional(),
});
export const ServerToDeviceFrame = z.discriminatedUnion("type", [
    HelloOkFrame,
    AckFrame,
    BlobAckFrame,
    BlobHaveFrame,
    PongFrame,
    CommandFrame,
]);
// ---------------------------------------------------------------------------------------------
// Browser ↔ server
// ---------------------------------------------------------------------------------------------
export const EventFilter = z.object({
    deviceId: z.string().optional(),
    runId: z.string().optional(),
    kinds: z.array(z.string()).optional(),
    excludeKinds: z.array(z.string()).optional(),
});
export const SubscribeFrame = z.object({
    type: z.literal("subscribe"),
    id: z.string(),
    filter: EventFilter.default({}),
    snapshot: z.object({ limit: z.number().int().positive().max(5000).default(200) }).optional(),
});
export const UnsubscribeFrame = z.object({ type: z.literal("unsubscribe"), id: z.string() });
export const BrowserFrame = z.discriminatedUnion("type", [SubscribeFrame, UnsubscribeFrame]);
/** In-memory filter match, shared by server fan-out and browser dedup/merge. */
export function matchesFilter(e, f) {
    if (f.deviceId && e.deviceId !== f.deviceId)
        return false;
    if (f.runId && e.runId !== f.runId)
        return false;
    if (f.kinds && f.kinds.length && !f.kinds.some((k) => kindMatches(e.kind, k)))
        return false;
    if (f.excludeKinds && f.excludeKinds.some((k) => kindMatches(e.kind, k)))
        return false;
    return true;
}
/** `graph.*` matches `graph.state`; exact otherwise. */
export function kindMatches(kind, pattern) {
    if (pattern.endsWith(".*"))
        return kind.startsWith(pattern.slice(0, -1));
    if (pattern.endsWith("*"))
        return kind.startsWith(pattern.slice(0, -1));
    return kind === pattern;
}
/** Encode a binary blob frame. */
export function encodeBlobFrame(header, bytes) {
    const h = new TextEncoder().encode(JSON.stringify(header));
    const out = new Uint8Array(4 + h.length + bytes.length);
    new DataView(out.buffer).setUint32(0, h.length, false);
    out.set(h, 4);
    out.set(bytes, 4 + h.length);
    return out;
}
/** Decode a binary blob frame. Throws on malformed input. */
export function decodeBlobFrame(buf) {
    if (buf.length < 4)
        throw new Error("blob frame too short");
    const len = new DataView(buf.buffer, buf.byteOffset, buf.byteLength).getUint32(0, false);
    if (4 + len > buf.length)
        throw new Error("blob header length out of range");
    const header = BlobHeader.parse(JSON.parse(new TextDecoder().decode(buf.subarray(4, 4 + len))));
    return { header, bytes: buf.subarray(4 + len) };
}
//# sourceMappingURL=protocol.js.map